import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { parseCoverDoc } from 'fym-shared/src/music24/coverDoc';
import { useGetCoverBackgroundsQuery, useGetCoverEditorQuery } from '../../redux/api/fymAPI';
import type { CoverBankBackground } from '../../redux/api/fymAPI.types';
import { useShowToast } from '../../redux/slices/toasts/hooks';
import { useQueryParam } from '../../utils/hooks/useQueryParam';
import { useIsDemoAccount } from '../accountSelector/useIsDemoAccount';
import { getFullAPIFileURL } from '../common/getFullAPIFileURL';
import { TypedMemo } from '../common/types';
import { ErrorView } from '../common/views/ErrorView/ErrorView';
import { LoadingView } from '../common/views/LoadingView';
import { useRealtimePollInterval } from '../realtime/useRealtimePollInterval';
import { catalogSongPath } from '../smartLinks/catalog/song/page/catalogSongPath';
import { CoverEditor } from './CoverEditor';
import { coverEditorMessages } from './coverEditorMessages';
import { safeReturnPath } from './coverEditorPath';
import { isCoverPictureWorking } from './panels/picture/aiPictures';
import { CoverThemeStep } from './states/CoverThemeStep';
import { firstOpenPlan, needsCoverThemeStep } from './states/firstOpen';
import { useFirstOpen } from './states/useFirstOpen';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from './CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The safety read of the AI pictures while the realtime socket is down. */
export const COVER_PICTURES_POLL_MS = 5000;
const NO_BACKGROUNDS: CoverBankBackground[] = [];

interface CoverEditorScreenProps {
  accountId: number;
  catalogItemUuid: string;
}

/**
 * The route body of the cover editor (design cover-editor b1, e1): it reads the song, its versions
 * and its AI pictures, and the bank; it makes the first version of a song with none (the editor
 * never opens empty); then it shows the editor on the open version. The realtime event
 * `cover.picture.updated` reads the pictures again; the poll runs only while the socket is down.
 */
const CoverEditorScreenComponent: React.FC<CoverEditorScreenProps> = ({ accountId, catalogItemUuid }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const toast = useShowToast();
  const readOnly = useIsDemoAccount();
  const from = useQueryParam('from', undefined, '');
  const [isWorking, setIsWorking] = useState(false);
  const pollingInterval = useRealtimePollInterval(isWorking ? COVER_PICTURES_POLL_MS : 0);
  const editor = useGetCoverEditorQuery({ accountId, catalogItemUuid }, { pollingInterval });
  const bank = useGetCoverBackgroundsQuery();
  const [openUuid, setOpenUuid] = useState<string | null>(null);
  // The theme step of a trial workspace (owner request 2026-10-09): pick the theme first, then the
  // first open makes the version and starts the 4 welcome pictures in that theme.
  const [isThemePicked, setIsThemePicked] = useState(false);

  const data = useMemo(() => {
    if (editor.data === undefined) {
      return undefined;
    }
    const { artworkUrl } = editor.data.song;
    return {
      ...editor.data,
      song: { ...editor.data.song, artworkUrl: artworkUrl === null ? null : getFullAPIFileURL(artworkUrl) },
    };
  }, [editor.data]);
  const hasWorking = useMemo(() => data?.pictures.some(isCoverPictureWorking) === true, [data]);
  useEffect(() => setIsWorking(hasWorking), [hasWorking]);
  // A bank that does not load is an empty bank: the editor still opens (a colour, an upload, AI).
  const backgrounds = bank.data?.backgrounds ?? (bank.isError ? NO_BACKGROUNDS : undefined);
  const showThemeStep =
    !readOnly &&
    !isThemePicked &&
    data !== undefined &&
    needsCoverThemeStep({ designCount: data.designs.length, welcomeRun: data.welcomeRun });
  const first = useFirstOpen({ accountId, catalogItemUuid, data, backgrounds, enabled: !readOnly && !showThemeStep });
  const { refetch } = editor;
  const themePicked = useCallback(async () => {
    // The song has its new theme: the first open reads it (bank picture, prompt) before it starts.
    await refetch();
    setIsThemePicked(true);
  }, [refetch]);

  const leave = useCallback(() => {
    // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
    void navigate(safeReturnPath(from) ?? catalogSongPath(catalogItemUuid, { section: 'details', accountId }));
  }, [accountId, catalogItemUuid, from, navigate]);
  const applied = useCallback(() => {
    toast.showSuccess(intl.formatMessage(coverEditorMessages.useDone));
    leave();
  }, [intl, leave, toast]);

  const design = useMemo(
    () => data?.designs.find((item) => item.uuid === (openUuid ?? first.result?.designUuid)) ?? data?.designs[0],
    [data, first.result, openUuid]
  );
  const initialDoc = useMemo(() => {
    if (data === undefined || backgrounds === undefined) {
      return null;
    }
    const saved = design === undefined ? null : parseCoverDoc(design.doc);
    // A version that does not parse (an older shape) and the example artist with no version get
    // the default first version; nothing is lost on the server until the user changes it.
    return (
      saved ??
      firstOpenPlan({
        catalogItemUuid,
        song: data.song,
        backgrounds,
        plan: data.plan,
        quota: data.quota,
        toUrl: getFullAPIFileURL,
      }).doc
    );
  }, [backgrounds, catalogItemUuid, data, design]);

  if (editor.error !== undefined && data === undefined) {
    return (
      <div className={styles.fullState}>
        <ErrorView error={editor.error} retry={() => editor.refetch().catch(console.error)} />
      </div>
    );
  }
  if (first.isFailed) {
    return (
      <div className={styles.fullState}>
        <ErrorView error={new Error(intl.formatMessage(coverEditorMessages.firstFailed))} retry={first.retry} />
      </div>
    );
  }
  if (showThemeStep && data !== undefined) {
    return (
      <div className={styles.fullState}>
        <CoverThemeStep
          accountId={accountId}
          catalogItemUuid={catalogItemUuid}
          title={data.song.title}
          themeId={data.song.themeId}
          pictureCount={data.welcomeRun?.pictures ?? 0}
          onDone={themePicked}
          onBack={leave}
        />
      </div>
    );
  }
  const isMakingFirst = !readOnly && data !== undefined && data.designs.length === 0;
  if (data === undefined || backgrounds === undefined || initialDoc === null || isMakingFirst) {
    return (
      <div className={styles.fullState} role="status">
        <LoadingView />
        {isMakingFirst && <p className={styles.fullStateText}>{intl.formatMessage(coverEditorMessages.firstMaking)}</p>}
      </div>
    );
  }
  const designUuid = design?.uuid ?? '';
  return (
    <CoverEditor
      key={designUuid}
      accountId={accountId}
      catalogItemUuid={catalogItemUuid}
      designUuid={designUuid}
      initialDoc={initialDoc}
      data={data}
      backgrounds={backgrounds}
      readOnly={readOnly}
      firstOpen={first.result !== null && first.result.designUuid === designUuid ? first.result : null}
      onOpenDesign={setOpenUuid}
      onLeave={leave}
      onApplied={applied}
    />
  );
};

export const CoverEditorScreen = TypedMemo(CoverEditorScreenComponent);
